import React, { useEffect, useRef, useState } from 'react'
import to from 'await-catch'
import { connect } from 'react-redux'
import SatisfactionSurvey from './index'
import SurveyInvitePopup from './SurveyInvitePopup'
import surveyApi from '../../api/survey'
import {
  setSurveyQuestions,
  setSurveyQuestionsError,
  setSurveyId,
  setSurveySubmitError,
  setSurveyCompleted
} from '../../actions'

const normalizeAnswer = value => ({
  questionId: value.questionId,
  answerId: value.answerId ?? value.id,
  comment: value.comment ?? null,
  answerDescription: value.answerDescription ?? value.description ?? null,
  answerMode: value.answerMode
})

const SatisfactionSurveyContainer = ({
  module,
  initialUrl,
  successMessage,
  buttonHidden,
  questions,
  displayOnClose,
  submitError,
  newAppointmentsBooked = 0,
  setSurveyQuestions,
  setSurveyQuestionsError,
  setSurveyId,
  setSurveySubmitError,
  setSurveyCompleted
}) => {
  const surveyIdRef = useRef(null)
  const answersRef = useRef([])
  const surveyTypeRef = useRef('automatic')
  const popupStatusRef = useRef(null)
  const [autoOpen, setAutoOpen] = useState(false)
  const [showInvitePopup, setShowInvitePopup] = useState(false)

  useEffect(() => {
    const fetchQuestions = async () => {
      const [err, res] = await to(surveyApi.getSurvey(initialUrl, module))
      if (err) {
        setSurveyQuestionsError(true)
      } else {
        const data = res?.data || res
        setSurveyQuestions({ questions: data.questions, displayOnClose: data.displayOnClose })
      }
    }
    fetchQuestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl, module])

  useEffect(() => {
    const fetchPopupStatus = async () => {
      const [err, res] = await to(surveyApi.getSurveyPopupStatus(initialUrl, module))
      if (err) return
      const data = res?.data || res
      if (data.mostrarPopup) {
        setShowInvitePopup(true)
      } else {
        popupStatusRef.current = {
          cantidadTurnos: data.cantidadTurnos,
          cantidadTurnosLimite: data.cantidadTurnosLimite
        }
      }
    }
    fetchPopupStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl, module])

  useEffect(() => {
    if (autoOpen || !newAppointmentsBooked || !popupStatusRef.current) return
    const { cantidadTurnos, cantidadTurnosLimite } = popupStatusRef.current
    if (cantidadTurnos + newAppointmentsBooked >= cantidadTurnosLimite) {
      setAutoOpen(true)
    }
  }, [newAppointmentsBooked, autoOpen])

  const handleSubmitAnswer = async (value, module, surveyType) => {
    const normalized = normalizeAnswer(value)
    answersRef.current.push(normalized)
    surveyTypeRef.current = surveyType

    const isLastAnswer = value.nextQuestionId === null
    if (isLastAnswer) return

    if (!surveyIdRef.current) {
      const [err, res] = await to(
        surveyApi.sendSurvey(module, { answers: answersRef.current }, surveyType)
      )
      if (err) {
        setSurveySubmitError(true)
      } else {
        const data = res?.data || res
        surveyIdRef.current = data.surveyId
        setSurveyId(data.surveyId)
      }
    } else {
      const [err] = await to(
        surveyApi.updateSurvey(module, surveyIdRef.current, { answers: [normalized] }, surveyType)
      )
      if (err) {
        setSurveySubmitError(true)
      }
    }
  }

  const handleComplete = async () => {
    if (surveyIdRef.current) {
      const [err] = await to(
        surveyApi.updateSurvey(
          module,
          surveyIdRef.current,
          { answers: answersRef.current },
          surveyTypeRef.current
        )
      )
      if (err) {
        setSurveySubmitError(true)
      } else {
        setSurveyCompleted(true)
      }
    } else {
      const [err, res] = await to(
        surveyApi.sendSurvey(module, { answers: answersRef.current }, surveyTypeRef.current)
      )
      if (err) {
        setSurveySubmitError(true)
      } else {
        const data = res?.data || res
        setSurveyId(data.surveyId)
        setSurveyCompleted(true)
      }
    }
    surveyIdRef.current = null
    answersRef.current = []
    setSurveyId(null)
  }

  const handleInvitePopupConfirm = () => {
    setShowInvitePopup(false)
    setAutoOpen(true)
  }

  const handleInvitePopupClose = () => {
    setShowInvitePopup(false)
  }

  return (
    <>
      <SurveyInvitePopup
        isOpen={showInvitePopup}
        onClose={handleInvitePopupClose}
        onConfirm={handleInvitePopupConfirm}
      />
      <SatisfactionSurvey
        module={module}
        questions={questions}
        successMessage={successMessage}
        buttonHidden={buttonHidden}
        hasError={submitError}
        autoOpen={autoOpen}
        onSubmitAnswer={handleSubmitAnswer}
        onComplete={handleComplete}
      />
    </>
  )
}

const mapDispatchToProps = {
  setSurveyQuestions,
  setSurveyQuestionsError,
  setSurveyId,
  setSurveySubmitError,
  setSurveyCompleted
}

function mapStateToProps({ survey }) {
  return {
    questions: survey.questions,
    displayOnClose: survey.displayOnClose,
    submitError: survey.submitError
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SatisfactionSurveyContainer)
